/**
 * What the front page knows, separated from how it looks.
 *
 * Every function here is pure. The page's real behaviour — which channels are
 * offered first, what a selection turns into, whether a room matches — is
 * decided here so it can be wrong in a test rather than in front of somebody.
 */

export interface FollowedChannel {
  readonly login: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly live: boolean;
  readonly gameName: string;
  readonly viewerCount: number;
}

export interface RoomCard {
  readonly id: string;
  readonly name: string;
  readonly slug: string | null;
  readonly kind: string;
  readonly channels: readonly string[];
  readonly lastSeenAt?: string;
  /** Absent on an ad-hoc room, which is neither listed nor unlisted. */
  readonly visibility?: 'public' | 'private';
  /** What the community says about itself, when it says anything. */
  readonly description?: string;
}

/**
 * Rooms minus the ones an earlier section has already shown.
 *
 * A room appearing twice on one page is read as two rooms — the name is the
 * same but the reader has no reason to assume the id is. The page shows the
 * same room at most once, in the highest section that has something to say
 * about it: yours, then where you have been, then the public shelf. Each
 * section is filtered by everything above it rather than the reverse, because
 * the top one is the one that has to be complete — a private room you made
 * and have not opened yet appears nowhere else on the page.
 */
export function withoutShown(
  rooms: readonly RoomCard[],
  alreadyShown: readonly RoomCard[],
): RoomCard[] {
  const seen = new Set(alreadyShown.map((r) => r.id));
  return rooms.filter((room) => !seen.has(room.id));
}

/**
 * An audience size, in the width a card has for it.
 *
 * Shown because the order of the live list depends on it: smallest audience
 * first is a deliberate choice (see `liveFirst`), and a list sorted by a
 * number nobody can see looks shuffled. Rounded rather than exact — the
 * difference between 1,203 and 1,240 viewers is not a fact anybody acts on,
 * and the extra digits cost the game name its room.
 */
export function formatViewers(count: number): string {
  if (!Number.isFinite(count) || count < 1) return '';
  if (count < 1_000) return String(Math.floor(count));
  if (count < 10_000) return `${(Math.floor(count / 100) / 10).toFixed(1)}K`;
  if (count < 1_000_000) return `${String(Math.floor(count / 1_000))}K`;
  return `${(Math.floor(count / 100_000) / 10).toFixed(1)}M`;
}

/**
 * The roster a room card can fit, and how many it could not.
 *
 * Separated from the card so the truncation is a tested rule rather than a
 * `slice` somebody tunes by eye: the `+N` has to agree with what is shown, and
 * the case that gets this wrong is the room with exactly one more channel than
 * fits.
 */
export function rosterPreview(
  channels: readonly string[],
  max = 3,
): { readonly shown: readonly string[]; readonly extra: number } {
  return { shown: channels.slice(0, max), extra: Math.max(0, channels.length - max) };
}

/**
 * Live follows, in the order the badges should appear.
 *
 * Smallest audience FIRST. The same choice as a community room's grid
 * (ADR 0043 §4) and for the same reason: sorting by viewers puts the same
 * four enormous channels at the front of everybody's page forever, and the
 * person streaming to nine viewers is the one who benefits from being seen.
 * A big channel is not hard to find.
 */
export function liveFirst(follows: readonly FollowedChannel[]): FollowedChannel[] {
  return follows
    .filter((channel) => channel.live)
    .sort((a, b) => {
      if (a.viewerCount !== b.viewerCount) return a.viewerCount - b.viewerCount;
      // Stable, so the row does not reshuffle between two equal channels
      // every time the page refreshes.
      return a.login.localeCompare(b.login);
    });
}

/** How many channels one room may open with. Beyond this nobody is watching. */
export const MAX_SELECTION = 8;

/**
 * Add or remove a channel from the selection, preserving click order.
 *
 * Order matters downstream: the first two selected are the ones that open
 * playing, so a Set — which is the obvious container — would silently decide
 * that for the viewer.
 */
export function toggleSelection(
  current: readonly string[],
  login: string,
  max = MAX_SELECTION,
): string[] {
  if (current.includes(login)) return current.filter((l) => l !== login);
  if (current.length >= max) return [...current];
  return [...current, login];
}

/**
 * The address for watching a selection right now, without saving anything.
 *
 * `/live/a+b+c` — the ad-hoc form. No room is created, nothing is listed, and
 * the URL describes itself, which is what makes it shareable by being copied.
 * Somebody who wants it to persist presses the other button.
 */
export function adhocHref(selection: readonly string[]): string | null {
  if (selection.length === 0) return null;
  /*
   * ONE CHANNEL USES THE PATH NOW, like every other count.
   *
   * It used to need the `?channels=` form, because a bare segment was
   * ambiguous: `/live/567` might be a community and `/live/stefancoat` a
   * streamer, and the app read a path as a channel list only when it held a
   * `+`. A single pick therefore went out as a link naming no channels and no
   * room that existed, and opened empty.
   *
   * ADR 0046 settled it: `/live/` is ad-hoc, always, so one name is one
   * channel and there is nothing to be mistaken for. The query form is still
   * READ, for links shared before this.
   */
  return `/live/${selection.map(encodeURIComponent).join('+')}`;
}

/**
 * Where a room lives. Its handle when it has one, because that is what people
 * share.
 *
 * `/c/`, not `/live/` (ADR 0046). A community handle and a Twitch login are
 * different namespaces now because they are different paths — which is the
 * only thing that stops a community called `stefancoat` taking the address
 * that names a streamer.
 */
export function roomHref(room: RoomCard): string {
  return `/c/${encodeURIComponent(room.slug ?? room.id)}`;
}

/**
 * A community's own page: `/{slug}` (ADR 0047), the directory in front of the
 * room. By id when it has no handle — the page asks the API, which answers to
 * either.
 */
export function communityHref(room: RoomCard): string {
  return `/${encodeURIComponent(room.slug ?? room.id)}`;
}

/**
 * How much of the selection a room already covers.
 *
 * Used to ORDER the "rooms with these streamers" list, and to say why each one
 * is there. A room matching four of your five is a better answer than one
 * matching one, and showing them in arrival order would bury it.
 */
export function overlapWith(
  room: RoomCard,
  selection: readonly string[],
): readonly string[] {
  const wanted = new Set(selection);
  return room.channels.filter((login) => wanted.has(login));
}

export function byOverlap(
  rooms: readonly RoomCard[],
  selection: readonly string[],
): { room: RoomCard; shared: readonly string[] }[] {
  return rooms
    .map((room) => ({ room, shared: overlapWith(room, selection) }))
    .filter((entry) => entry.shared.length > 0)
    .sort((a, b) => {
      if (a.shared.length !== b.shared.length) return b.shared.length - a.shared.length;
      // Then the smaller room: one that watches exactly your three is a
      // closer answer than one that watches your three and twenty others.
      return a.room.channels.length - b.room.channels.length;
    });
}

/**
 * A name for a room somebody has not named.
 *
 * Used as the default in the create field so the button is never disabled on
 * an empty box. Two names and a count reads better than a list of eight that
 * the field cannot show.
 */
export function suggestedRoomName(
  selection: readonly string[],
  display: (login: string) => string = (l) => l,
): string {
  if (selection.length === 0) return 'My room';
  const names = selection.map(display);
  if (names.length === 1) return `${names[0] ?? ''} and friends`;
  if (names.length === 2) return `${names[0] ?? ''} & ${names[1] ?? ''}`;
  return `${names[0] ?? ''}, ${names[1] ?? ''} +${String(names.length - 2)}`;
}

/** How many of your rooms show before "Show all". */
export const YOUR_ROOMS_SHOWN = 3;

/**
 * How many live follows show before "Show all".
 *
 * Nine because it is three full rows at the page's width, so the fold never
 * lands halfway through a row and reads as a list that stopped loading.
 */
export const LIVE_SHOWN = 9;

/**
 * Your rooms, most recently used first.
 *
 * ONE LIST, where there used to be two. "Rooms you run" and "where you have
 * been" answered who made a room rather than which one somebody wants back,
 * and the second question is the one a front page is opened with. So both
 * sources merge here and recency alone orders them.
 *
 * `owned` has no visit time of its own — membership is not a visit — so it
 * borrows one from the visit log. A room you run and have never opened sorts
 * after every room you have, in the order the server gave, rather than being
 * dropped: it is unlisted more often than not, and this is the only place it
 * can be found.
 */
export function yourRooms(
  owned: readonly RoomCard[],
  visited: readonly RoomCard[],
): RoomCard[] {
  const seenAt = new Map(visited.map((r) => [r.id, r.lastSeenAt]));
  const merged = [
    ...owned.map((room) => {
      const at = room.lastSeenAt ?? seenAt.get(room.id);
      return at === undefined ? room : { ...room, lastSeenAt: at };
    }),
    ...withoutShown(visited, owned),
  ];
  return byRecency(merged);
}

/** Visited first, newest first; never-visited after, in the order given. */
function byRecency(rooms: readonly RoomCard[]): RoomCard[] {
  // `sort` is stable, so equal keys keep their arrival order.
  return [...rooms].sort((a, b) => {
    const at = a.lastSeenAt ?? '';
    const bt = b.lastSeenAt ?? '';
    return at === bt ? 0 : at < bt ? 1 : -1;
  });
}

export type ShelfSort = 'recommended' | 'active' | 'recent';

export const SHELF_SORTS: readonly { readonly key: ShelfSort; readonly label: string }[] = [
  { key: 'recommended', label: 'For you' },
  { key: 'active', label: 'Most active' },
  { key: 'recent', label: 'Last visited' },
];

export function isShelfSort(value: unknown): value is ShelfSort {
  return SHELF_SORTS.some((s) => s.key === value);
}

/**
 * Which of somebody's follows a room watches, live ones first.
 *
 * The reason a room is recommended, so it is also what the card says: a
 * suggestion nobody can see the reason for reads as an advert.
 */
export function followedIn(
  room: RoomCard,
  follows: readonly FollowedChannel[],
): { readonly live: readonly string[]; readonly offline: readonly string[] } {
  const byLogin = new Map(follows.map((f) => [f.login, f]));
  const live: string[] = [];
  const offline: string[] = [];
  for (const login of room.channels) {
    const f = byLogin.get(login);
    if (f === undefined) continue;
    (f.live ? live : offline).push(login);
  }
  return { live, offline };
}

/**
 * The public shelf, in the order somebody asked for.
 *
 * - `recommended`: rooms that watch people you follow. A follow who is live
 *   RIGHT NOW counts for more than one who is not, because the question the
 *   page is opened with is what to watch tonight, not whose rooms exist.
 * - `active`: most people in the room now, then most of your follows live in
 *   it — the second key is what separates the many rooms nobody is in yet.
 * - `recent`: your visit log. Rooms you have never opened follow, unchanged.
 *
 * Every mode is a sort, never a filter. The shelf is the same length whatever
 * is pressed, so switching modes cannot read as rooms disappearing.
 */
export function sortShelf(
  rooms: readonly RoomCard[],
  mode: ShelfSort,
  context: {
    readonly follows: readonly FollowedChannel[];
    readonly occupancy: (roomId: string) => number;
    readonly lastSeen: (roomId: string) => string | undefined;
  },
): RoomCard[] {
  if (mode === 'recent') {
    return byRecency(rooms.map((room) => {
      const at = context.lastSeen(room.id);
      return at === undefined ? room : { ...room, lastSeenAt: at };
    }));
  }
  const keyed = rooms.map((room) => {
    const { live, offline } = followedIn(room, context.follows);
    return {
      room,
      affinity: live.length * 3 + offline.length,
      live: live.length,
      here: context.occupancy(room.id),
    };
  });
  keyed.sort((a, b) => mode === 'recommended'
    ? (b.affinity - a.affinity) || (b.here - a.here)
    : (b.here - a.here) || (b.live - a.live));
  return keyed.map((k) => k.room);
}

/**
 * Ids in groups the presence route will take.
 *
 * It answers for the first thirty ids and drops the rest (Firestore's `in`
 * limit), so a shelf longer than that asked in one request had no count for
 * any room past the thirtieth — and "most active" would sort those as empty
 * whether they were or not.
 */
export function inBatches<T>(items: readonly T[], size = 30): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
